import { useEffect, useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { IDetailsQuotationData } from 'types/Order.types';
import { AppMode } from 'store/Common/reducers/common';
import { Input } from 'components/Input/InputFormik';
import ReadonlyInput from 'components/Input/Input';
import CustomSelect from 'components/Select/FormikSelect';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { useGetCountryOptions } from 'utils/hooks/useGetCountryOptions';
import { useGetShoppingCart } from 'pages/ShoppingCartPage/hooks';
import { useActiveCartId } from 'utils/hooks/useActiveCartId';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import { titleOptions } from './constants';
import { quotationDetailsSchema } from './validation';
import FormObserver from './FormObserver';
import {
  AlternativeAddressRow,
  Card,
  Container,
  Divider,
  GroupLabel,
  Row,
  SectionTitle,
  SelectWrapper,
  StyledSelect,
} from './QuotationCheckoutPage.styled';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';

type IQuotationDetailsFormValues = Omit<IDetailsQuotationData, 'validDetails'>;

const QuotationCheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const formikRef = useRef<FormikProps<IQuotationDetailsFormValues> | null>(
    null
  );

  useEffect(() => {
    formikRef.current?.validateForm();
  }, [i18n.language]);

  const { isTablet, isSmallTablet } = useBreakpointFlag();

  const countryOptions = useGetCountryOptions();

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  const shoppingCartId = useActiveCartId();
  const detailsQuotationData = useSelector(
    (state: IRootReducerState) => state.orderInfo.detailsQuotationData
  );

  const { data: cartData } = useGetShoppingCart(appMode, shoppingCartId);
  const formatMoney = useFormatMoney(cartData?.currency);

  const { ...initialValues } = detailsQuotationData;

  return (
    <Container>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={quotationDetailsSchema(t)}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={true}
        onSubmit={() => undefined}
      >
        {({
          values,
          errors: formErrors,
          touched,
          handleBlur,
          setFieldValue,
        }) => {
          const errors = formErrors as Record<string, string | undefined>;
          return (
            <Card>
              <FormObserver />

              <SectionTitle>{t('Customer details')}</SectionTitle>

              <GroupLabel>{t('Name')}</GroupLabel>
              <Row>
                <Input
                  name="first_name"
                  height={'41rem'}
                  placeholder={t('First name')}
                  errorMessage={touched.first_name ? errors.first_name : ''}
                  onBlur={(e) => {
                    setFieldValue('first_name', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  name="last_name"
                  height={'41rem'}
                  placeholder={t('Last name')}
                  errorMessage={touched.last_name ? errors.last_name : ''}
                  onBlur={(e) => {
                    setFieldValue('last_name', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />
                <SelectWrapper>
                  <StyledSelect
                    name="title"
                    height={'41rem'}
                    options={titleOptions}
                    component={CustomSelect}
                    placeholder={t('Choose')}
                    isMulti={false}
                    errorMessage={touched.title ? errors.title : ''}
                  />
                </SelectWrapper>
              </Row>

              <GroupLabel>{t('Contact')}</GroupLabel>
              <Row>
                <Input
                  name="email"
                  height={'41rem'}
                  placeholder={t('Email')}
                  errorMessage={touched.email ? errors.email : ''}
                  onBlur={(e) => {
                    setFieldValue('email', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '40%' : '100%',
                  }}
                />

                <Input
                  name="phone"
                  height={'41rem'}
                  placeholder={t('Phone number')}
                  errorMessage={touched.phone ? errors.phone : ''}
                  onBlur={(e) => {
                    setFieldValue('phone', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '40%' : '100%',
                  }}
                />
              </Row>

              <GroupLabel>{t('Address')}</GroupLabel>
              <Row>
                <Input
                  name="zip_code"
                  height={'41rem'}
                  placeholder={t('Zip code')}
                  errorMessage={touched.zip_code ? errors.zip_code : ''}
                  onBlur={(e) => {
                    setFieldValue('zip_code', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  name="house_number"
                  height={'41rem'}
                  placeholder={t('House number')}
                  errorMessage={touched.house_number ? errors.house_number : ''}
                  onBlur={(e) => {
                    setFieldValue('house_number', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  name="city"
                  height={'41rem'}
                  placeholder={t('City')}
                  errorMessage={touched.city ? errors.city : ''}
                  onBlur={(e) => {
                    setFieldValue('city', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  name="street"
                  height={'41rem'}
                  placeholder={t('Street')}
                  errorMessage={touched.street ? errors.street : ''}
                  onBlur={(e) => {
                    setFieldValue('street', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />
                <SelectWrapper>
                  <StyledSelect
                    name="country"
                    height={'41rem'}
                    options={countryOptions}
                    component={CustomSelect}
                    placeholder={t('Country')}
                    isMulti={false}
                    errorMessage={touched.country ? errors.country : ''}
                  />
                </SelectWrapper>
              </Row>

              <GroupLabel>{t('Company')}</GroupLabel>
              <Row>
                <Input
                  name="company_name"
                  height={'41rem'}
                  placeholder={t('Company name')}
                  errorMessage={touched.company_name ? errors.company_name : ''}
                  onBlur={(e) => {
                    setFieldValue('company_name', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '40%' : '100%',
                  }}
                />

                <Input
                  name="vat_number"
                  height={'41rem'}
                  placeholder={t('Company VAT')}
                  errorMessage={touched.vat_number ? errors.vat_number : ''}
                  onBlur={(e) => {
                    setFieldValue('vat_number', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '40%' : '100%',
                  }}
                />
              </Row>

              <AlternativeAddressRow>
                <Checkbox
                  label={t('Alternative shipping address')}
                  isChecked={values.hasAlternativeShippingAddress}
                  onChange={(checked: boolean) =>
                    setFieldValue('hasAlternativeShippingAddress', checked)
                  }
                />
              </AlternativeAddressRow>

              {values.hasAlternativeShippingAddress && (
                <Row>
                  <Input
                    name="alt_zip_code"
                    height={'41rem'}
                    placeholder={t('Zip code')}
                    errorMessage={
                      touched.alt_zip_code ? errors.alt_zip_code : ''
                    }
                    onBlur={(e) => {
                      setFieldValue('alt_zip_code', e.target.value.trim());
                      handleBlur(e);
                    }}
                    wrapperStyles={{
                      width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                    }}
                  />

                  <Input
                    name="alt_house_number"
                    height={'41rem'}
                    placeholder={t('House number')}
                    errorMessage={
                      touched.alt_house_number ? errors.alt_house_number : ''
                    }
                    onBlur={(e) => {
                      setFieldValue('alt_house_number', e.target.value.trim());
                      handleBlur(e);
                    }}
                    wrapperStyles={{
                      width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                    }}
                  />

                  <Input
                    name="alt_city"
                    height={'41rem'}
                    placeholder={t('City')}
                    errorMessage={touched.alt_city ? errors.alt_city : ''}
                    onBlur={(e) => {
                      setFieldValue('alt_city', e.target.value.trim());
                      handleBlur(e);
                    }}
                    wrapperStyles={{
                      width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                    }}
                  />

                  <Input
                    name="alt_street"
                    height={'41rem'}
                    placeholder={t('Street')}
                    errorMessage={touched.alt_street ? errors.alt_street : ''}
                    onBlur={(e) => {
                      setFieldValue('alt_street', e.target.value.trim());
                      handleBlur(e);
                    }}
                    wrapperStyles={{
                      width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                    }}
                  />
                  <SelectWrapper>
                    <StyledSelect
                      name="alt_country"
                      height={'41rem'}
                      options={countryOptions}
                      component={CustomSelect}
                      placeholder={t('Country')}
                      isMulti={false}
                      errorMessage={
                        touched.alt_country ? errors.alt_country : ''
                      }
                    />
                  </SelectWrapper>
                </Row>
              )}

              <Divider />

              <SectionTitle>{t('Quotation details')}</SectionTitle>
              <Row>
                <Input
                  name="days_valid"
                  height={'41rem'}
                  type="number"
                  placeholder={t('Days valid')}
                  errorMessage={touched.days_valid ? errors.days_valid : ''}
                  onBlur={(e) => {
                    setFieldValue('days_valid', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  name="reference"
                  height={'41rem'}
                  placeholder={t('Reference')}
                  errorMessage={touched.reference ? errors.reference : ''}
                  onBlur={(e) => {
                    setFieldValue('reference', e.target.value.trim());
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <ReadonlyInput
                  placeholder={''}
                  disabled
                  readOnly
                  value={
                    cartData?.total_net_price !== undefined
                      ? formatMoney(cartData.total_net_price)
                      : ''
                  }
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />
              </Row>
            </Card>
          );
        }}
      </Formik>
    </Container>
  );
};

export default QuotationCheckoutPage;
